import React, { useState, useEffect } from 'react';
import { X, ChevronRight, Sparkles, Crown, Lock } from 'lucide-react';

const FeatureTour = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [show, setShow] = useState(true);
  
  const tourSteps = [
    {
      title: 'Welcome to Your Dashboard!',
      description: 'Let\'s take a quick tour of the powerful features available to help you optimize your mortgage renewal.',
      highlight: null,
      position: 'center'
    },
    {
      title: 'Overview Tab',
      description: 'Get a bird\'s eye view of your mortgage situation with key metrics, days to renewal, and best available rates.',
      highlight: '[data-testid="tab-overview"]',
      position: 'bottom',
      icon: '📊'
    },
    {
      title: 'Renewal Decision Engine',
      description: 'Our intelligent recommendation system analyzes market conditions and your timeline to suggest optimal renewal timing.',
      highlight: null,
      position: 'center',
      icon: '🧠',
      premium: true,
      tier: 'Premium'
    },
    {
      title: 'Mortgage Details',
      description: 'Update and track all your mortgage information in one place. Your data is automatically saved.',
      highlight: '[data-testid="tab-details"]',
      position: 'bottom',
      icon: '🏠'
    },
    {
      title: 'Market Rates',
      description: 'View live rates from major Canadian lenders, economic indicators, and rate forecasts to time your renewal perfectly.',
      highlight: '[data-testid="tab-rates"]',
      position: 'bottom',
      icon: '📈',
      premium: true,
      tier: 'Premium'
    },
    {
      title: 'HELOC Calculator',
      description: 'Calculate your Home Equity Line of Credit potential and explore different prepayment scenarios to accelerate your mortgage payoff.',
      highlight: '[data-testid="tab-strategies"]',
      position: 'bottom',
      icon: '💰',
      premium: true,
      tier: 'Pro'
    },
    {
      title: 'Goals & Progress',
      description: 'Set mortgage goals and track your renewal journey with our guided checklist. Stay organized and never miss a deadline.',
      highlight: '[data-testid="tab-goals"]',
      position: 'bottom',
      icon: '🎯'
    },
    {
      title: 'Subscription Plans',
      description: 'Unlock premium features with our affordable subscription plans. Start with Free and upgrade anytime to access advanced tools.',
      highlight: '[data-testid="tab-settings"]',
      position: 'bottom',
      icon: '⭐'
    }
  ];
  
  const currentStepData = tourSteps[currentStep];
  
  useEffect(() => {
    if (currentStepData?.highlight) {
      const element = document.querySelector(currentStepData.highlight);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('ring-4', 'ring-primary', 'ring-offset-2', 'ring-offset-background');
      }
      
      return () => {
        if (element) {
          element.classList.remove('ring-4', 'ring-primary', 'ring-offset-2', 'ring-offset-background');
        }
      };
    }
  }, [currentStep, currentStepData]);
  
  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };
  
  const handleSkip = () => {
    handleComplete();
  };
  
  const handleComplete = () => {
    localStorage.setItem('tour_completed', 'true');
    setShow(false);
    setTimeout(() => onComplete(), 300);
  };
  
  if (!show) return null;
  
  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-opacity" />
      
      {/* Tour Card */}
      <div className={`fixed z-50 ${
        currentStepData.position === 'center'
          ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
          : 'bottom-8 left-1/2 -translate-x-1/2'
      } w-full max-w-lg mx-4 transition-all`}>
        <div className="bg-card border-2 border-primary rounded-xl shadow-2xl p-6 animate-in fade-in slide-in-from-bottom-4">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              {currentStepData.icon && (
                <span className="text-3xl">{currentStepData.icon}</span>
              )}
              <div>
                <h3 className="text-xl font-heading font-semibold flex items-center gap-2">
                  {currentStepData.title}
                  {currentStepData.premium && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full">
                      {currentStepData.tier === 'Pro' ? <Crown className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
                      {currentStepData.tier}
                    </span>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Step {currentStep + 1} of {tourSteps.length}
                </p>
              </div>
            </div>
            <button
              onClick={handleSkip}
              data-testid="tour-skip-btn"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <p className="text-foreground mb-6">
            {currentStepData.description}
          </p>
          
          {currentStepData.premium && (
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 mb-6">
              <div className="flex items-start gap-2">
                <Lock className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-primary">Premium Feature</p>
                  <p className="text-muted-foreground">
                    Upgrade to {currentStepData.tier} to unlock this powerful feature.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <div className="flex gap-1">
              {tourSteps.map((_, idx) => (
                <div
                  key={idx}
                  className={`h-2 w-2 rounded-full transition-all ${
                    idx === currentStep
                      ? 'bg-primary w-6'
                      : idx < currentStep
                      ? 'bg-primary/50'
                      : 'bg-muted'
                  }`}
                />
              ))}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleSkip}
                className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Skip Tour
              </button>
              <button
                onClick={handleNext}
                data-testid="tour-next-btn"
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
              >
                {currentStep < tourSteps.length - 1 ? 'Next' : 'Get Started'}
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FeatureTour;